import { useState, type FC } from "react";
import { Link } from "@tanstack/react-router";
import { MaxWidthWrapper } from "@/components/maxWidthWrapper";
import { useMotionValueEvent, useScroll, motion } from "framer-motion";
import { cn } from "@/utils/cn";
import { ThemeToggle } from "@/components/theme-toggle";
import { NavLogo as LogoWithText } from "@/components/navLogo";

const NavLogo = () => {
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (window.location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Link to="/" className="flex items-center gap-2" onClick={handleLogoClick}>
      <LogoWithText className="h-12 w-auto text-primary" />
    </Link>
  );
};

/*
import { useAuth } from "@/hooks/useAuth";
import { LoginButton } from "@/components/LoginButton";
import { UserMenu } from "@/components/UserMenu";
const AuthControls = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  return user ? <UserMenu /> : <LoginButton />;
};
*/

interface DesktopNavbarProps {
  tabs: readonly {
    id: string;
    label: string;
    desktopNavTab?: { label: string; href: string };
  }[];
  onTabClick: (id: string) => void;
}

export const DesktopNavbar: FC<DesktopNavbarProps> = ({ tabs, onTabClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 80) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  });

  return (
    <motion.nav
      initial={{ opacity: 0, y: "-100%" }}
      animate={{ opacity: 1, y: "0%" }}
      transition={{ duration: 1.25, ease: "linear" }}
      className={cn(
        "fixed top-0 right-0 left-0 z-50 py-2",
        scrolled ? "bg-background" : "bg-transparent",
      )}
    >
      <MaxWidthWrapper>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-12">
            <NavLogo />
            <div className="hidden md:block">
              <ul className="flex gap-3 md:gap-9">
                {tabs.map((tab, i) => {
                  // don't render the home tab, it's handled by the logo
                  if (i === 0) return null;
                  return (
                    <li key={tab.id}>
                      <button
                        className="group relative flex items-center gap-0.5 px-1 py-2 text-xs transition-colors hover:text-muted-foreground sm:text-sm"
                        onClick={() => onTabClick(tab.id)}
                      >
                        {tab.label}
                        <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* nav right - AuthControls will go here */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </MaxWidthWrapper>
    </motion.nav>
  );
};
